import type { ControllerContract } from '../architecture/ControllerContract';
import {
    assistantRoutes,
    type AssistantRoutes
} from '../routes/AssistantRoutes';
import { chatRoutes, type ChatRoutes } from '../routes/ChatRoutes';
import {
    customerSessionRoutes,
    type CustomerSessionRoutes
} from '../routes/CustomerSessionRoutes';
import { productRoutes, type ProductRoutes } from '../routes/ProductRoutes';
import type {
    EndpointCallOptions,
    IControllerContractSlice,
    IPublicApiClient
} from './IPublicApiClient';
import { PublicApiError } from './PublicApiError';

export class PublicApiClient
    implements
        IPublicApiClient<
            [ProductRoutes, AssistantRoutes, CustomerSessionRoutes]
        >
{
    baseUrl: string;
    accountId: string;
    tenantId?: string;
    chat: IControllerContractSlice<ChatRoutes>;
    assistant: IControllerContractSlice<AssistantRoutes>;
    product: IControllerContractSlice<ProductRoutes>;
    ['customer-session']: IControllerContractSlice<CustomerSessionRoutes>;

    constructor(baseUrl: string, accountId: string, tenantId?: string) {
        this.baseUrl = baseUrl;
        this.accountId = accountId;
        if (tenantId !== undefined) {
            this.tenantId = tenantId;
        }
        this.chat = generateControllerContractSlice.call(this, chatRoutes);
        this.assistant = generateControllerContractSlice.call(
            this,
            assistantRoutes
        );
        this.product = generateControllerContractSlice.call(
            this,
            productRoutes
        );
        this.chat = generateControllerContractSlice.call(this, chatRoutes);
        this['customer-session'] = generateControllerContractSlice.call(
            this,
            customerSessionRoutes
        );
    }

    public setTenantId(tenantId: string): void {
        this.tenantId = tenantId;
    }
}

function generateControllerContractSlice<C extends ControllerContract>(
    this: PublicApiClient,
    controllerContract: C
): IControllerContractSlice<C> {
    const slice = {} as Record<string, unknown>;
    for (const endpoint in controllerContract.endpoints) {
        slice[endpoint] = async (args: {
            params?: Record<string, unknown>;
            body?: NonNullable<unknown>;
            signal?: AbortSignal | null;
            options?: EndpointCallOptions;
        }) => {
            const { body, params, signal, options } = args;
            const path = controllerContract.endpoints[endpoint].path;
            const parsedPath = replaceParamsInPath(path, params);
            const url = `${this.baseUrl}/${controllerContract.controllerPath}/${parsedPath}`;
            const method = controllerContract.endpoints[endpoint].verb;
            const bodySchema =
                controllerContract.endpoints[endpoint].schema.body;
            const successResponseSchema =
                controllerContract.endpoints[endpoint].schema.response?.[200];
            const opts = { authenticated: true, ...options };
            const rawResponse = await fetch(url, {
                method,
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...(opts.authenticated && {
                        Authorization: `Bearer v1 ${this.accountId} ${this.tenantId}`
                    }),
                    ...opts.headers
                },
                ...(bodySchema && {
                    body: JSON.stringify(body)
                }),
                signal
            });

            if (!rawResponse.ok) {
                throw new PublicApiError(
                    controllerContract,
                    endpoint,
                    rawResponse
                );
            }

            return successResponseSchema ? await rawResponse.json() : undefined;
        };
    }
    return slice as IControllerContractSlice<C>;
}

function replaceParamsInPath(
    path: string,
    params?: Record<string, unknown>
): string {
    if (!params) {
        return path;
    }
    return Object.keys(params).reduce((previous, current) => {
        return path.replace(
            `:${current}`,
            `${formatValueForParam(params[current])}`
        );
    }, path);
}

function formatValueForParam(value: unknown): string {
    if (value instanceof Date) {
        return value.toJSON();
    }
    return `${value}`;
}
